import { useContext, useEffect, useState } from "react";
import {
  InputMessageComponent,
  InputMessageContainer,
  MessageInputContainer,
  PriorityChip,
  PriorityMessageContainer,
  SpeedDialContainer,
} from "../MessagePanel.style";
import {
  ForwardMessageContext,
  MessagePanelContext,
  OrganizationContext,
  ReplyMessageContext,
  useTranslation,
} from "hhn-commons";
import SpeedDialComponent from "../messageFooter/SpeedDial";
import {
  IEditMessagePayload,
  IFileAndCaption,
  ImmutableChat,
  ISendMessagePayload,
  MESSAGE_TYPE,
  SEND_MESSAGE_TYPE,
} from "@home-health-notify/domain";
import { InputMessageBox } from "./InputMessageBox";
import { MessageActionButtons } from "./MessageActionButtons";
import { PendingAckStrip } from "./PendingAckStrip";
import { PendingReferralStrip } from "./PendingReferralStrip";
import { ConfirmationDialog } from "../../common/ConfirmationDialog";
import { EditMessageBanner } from "./EditMessageBanner";
import { ISubmitSocketResponse } from "@home-health-notify/models";
interface InputMessageProps {
  chat: ImmutableChat;
  selectFile: () => void;

  sendMessage(
    payload: ISendMessagePayload,
    sendMessageType: SEND_MESSAGE_TYPE,
  ): void;
  editMessage(
    payload: IEditMessagePayload,
    responseCallback?: (
      data: ISubmitSocketResponse,
      payload?: IEditMessagePayload,
    ) => void,
    onTimeout?: () => void,
  ): void;
  onPasteFile: (fileList: IFileAndCaption[]) => Promise<void>;
}

interface MentionedUserTypes {
  hasPatient: boolean;
  hasPhysician: boolean;
}

export const InputMessage = ({
  chat,
  selectFile,
  sendMessage,
  editMessage,
  onPasteFile,
}: InputMessageProps) => {
  const { t } = useTranslation();
  const tNs = (key: string): string => t(key, { ns: "message" });

  const { isForwardModeOn } = useContext(ForwardMessageContext);
  const { scrollToBottom, messageToEdit, setMessageToEdit } =
    useContext(MessagePanelContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [chatMessage, setChatMessage] = useState("");
  const [chatMessagePlainText, setChatMessagePlainText] = useState("");
  const [isPriority, setPriority] = useState(false);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);
  const [mentionedUserTypes, setMentionedUserTypes] =
    useState<MentionedUserTypes>({
      hasPatient: false,
      hasPhysician: false,
    });

  const { replyToMessage, cancelReply } = useContext(ReplyMessageContext);

  const isChatMessageEmpty = chatMessage.length === 0;

  const isEditingPriorityMessage =
    messageToEdit?.getMessage().type === MESSAGE_TYPE.PRIORITY;

  useEffect(() => {
    resetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.chatInfo.id]);

  useEffect(() => {
    if (!messageToEdit) {
      return;
    }

    const messageData = messageToEdit.getMessage();
    const existingMentionedUsers = messageData.mentionedUsers ?? [];

    let messageWithMentionMarkup = messageData.body;

    existingMentionedUsers.forEach(userId => {
      const userInfo = selectedOrgUser?.userDirectory.getUserInfo(userId);

      if (userInfo?.title) {
        messageWithMentionMarkup = messageWithMentionMarkup.replaceAll(
          `@${userInfo.title}`,
          `@[${userInfo.title}]`,
        );
      }
    });
    setMentionedUsers(existingMentionedUsers);
    setChatMessage(messageWithMentionMarkup);
    setChatMessagePlainText(messageData.body);
    setOpenSpeedDial(false);
  }, [messageToEdit, selectedOrgUser]);

  const cancelEditMessage = () => {
    setMessageToEdit(undefined);
    setChatMessage("");
    setChatMessagePlainText("");
    setMentionedUsers([]);
    setOpenSpeedDial(false);
  };

  const resetData = () => {
    setChatMessage("");
    setMentionedUsers([]);
    setChatMessagePlainText("");
    setPriority(false);
    setMentionedUserTypes({ hasPatient: false, hasPhysician: false });
    setMessageToEdit(undefined);
    cancelReply();
  };

  const checkMentionedUserTypes = (): MentionedUserTypes => {
    let hasPatient = false;
    let hasPhysician = false;

    mentionedUsers.forEach(userId => {
      const userInfo = selectedOrgUser?.userDirectory.getUserInfo(userId);
      if (userInfo?.type === "PATIENT") {
        hasPatient = true;
      } else if (userInfo?.type === "PHYSICIAN") {
        hasPhysician = true;
      }
    });

    return { hasPatient, hasPhysician };
  };

  const send = async () => {
    if (chatMessagePlainText) {
      if (mentionedUsers.length > 0) {
        const userTypes = checkMentionedUserTypes();
        setMentionedUserTypes(userTypes);

        // Show confirmation dialog if patient or physician is mentioned
        if (userTypes.hasPatient || userTypes.hasPhysician) {
          setShowConfirmDialog(true);
          return;
        }
      }
      handleConfirmSend();
    }
  };

  const getDialogMessage = (): string => {
    if (mentionedUserTypes.hasPatient && mentionedUserTypes.hasPhysician) {
      return tNs("patientConfirmation.dialogMessageBoth");
    } else if (mentionedUserTypes.hasPatient) {
      return tNs("patientConfirmation.dialogMessage");
    } else if (mentionedUserTypes.hasPhysician) {
      return tNs("patientConfirmation.dialogMessagePhysician");
    }
    return tNs("patientConfirmation.dialogMessage");
  };

  const handleConfirmSend = (isVisibleToPatient: boolean = false) => {
    const visibilityPayload = {
      ...(mentionedUserTypes.hasPatient &&
        isVisibleToPatient && {
          isVisibleToPatient: true,
        }),
      ...(mentionedUserTypes.hasPhysician && {
        isVisibleToPhysician: true,
      }),
    };

    if (messageToEdit) {
      const originalMessage = messageToEdit.getMessage();

      const editPayload: IEditMessagePayload = {
        _id: originalMessage.id,
        body: chatMessagePlainText,
        mentionedUsers: isEditingPriorityMessage ? [] : mentionedUsers,
        ...visibilityPayload,
      };

      editMessage(editPayload);

      resetData();
      scrollToBottom?.();
      setShowConfirmDialog(false);
      return;
    }
    const sendPayload: ISendMessagePayload = {
      body: chatMessagePlainText,
      ...(mentionedUsers.length > 0 && { mentionedUsers }),
      ...(replyToMessage && {
        replyToMessageData: replyToMessage.getMessage(),
      }),
      ...visibilityPayload,
    };

    sendMessage(
      sendPayload,
      isPriority ? SEND_MESSAGE_TYPE.PRIORITY : SEND_MESSAGE_TYPE.NORMAL,
    );

    resetData();
    scrollToBottom?.();
    setShowConfirmDialog(false);
  };

  const onRemove = (id: string) => {
    setMentionedUsers(currentUsers =>
      currentUsers.filter(userId => userId !== id),
    );
  };
  const onAdd = (id: string) => {
    setMentionedUsers(currentUsers =>
      currentUsers.includes(id) ? currentUsers : [...currentUsers, id],
    );
  };
  const onChange = (
    event: React.ChangeEvent<HTMLInputElement> | unknown,
    newValue: string,
    newPlainTextValue: string,
  ) => {
    setChatMessage(newValue.trimStart());
    setChatMessagePlainText(newPlainTextValue.trim());
  };

  const onOpen = () => setOpenSpeedDial(true);
  const onClose = () => setOpenSpeedDial(false);

  return (
    <>
      {!isForwardModeOn && (
        <InputMessageContainer>
          <PendingAckStrip chatAccess={chat} />
          <PendingReferralStrip chatAccess={chat} />
          <InputMessageComponent>
            {isPriority && (
              <PriorityMessageContainer>
                <PriorityChip
                  label={tNs("message.priorityLabel")}
                  color="error"
                />
              </PriorityMessageContainer>
            )}
            {messageToEdit && (
              <EditMessageBanner onCancel={cancelEditMessage} />
            )}
            <MessageInputContainer>
              {!replyToMessage && (
                <SpeedDialContainer>
                  <SpeedDialComponent
                    chat={chat}
                    selectFile={selectFile}
                    open={openSpeedDial}
                    onOpen={onOpen}
                    onClose={onClose}
                    sendMessage={sendMessage}
                    isPriority={isPriority}
                    setPriority={setPriority}
                  />
                </SpeedDialContainer>
              )}
              <InputMessageBox
                chat={chat}
                chatMessage={chatMessage}
                onAdd={onAdd}
                onChange={onChange}
                send={send}
                replyMessage={replyToMessage ?? undefined}
                onRemove={onRemove}
                mentionedUsers={mentionedUsers}
                onPasteFile={onPasteFile}
                disableMention={isEditingPriorityMessage}
              />
              <MessageActionButtons
                isChatMessageEmpty={isChatMessageEmpty}
                isPriority={isPriority}
                replyMessage={replyToMessage ?? undefined}
                send={send}
                setPriority={setPriority}
                cancelReplyMode={cancelReply}
              />
            </MessageInputContainer>
          </InputMessageComponent>
        </InputMessageContainer>
      )}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        confirmationTitle={tNs("patientConfirmation.dialogTitle")}
        confirmationMessage={getDialogMessage()}
        onConfirmationAction={() => handleConfirmSend(true)}
        confirmationButtonText={tNs(
          "patientConfirmation.confirmationButtonText",
        )}
        confirmationCancelText={tNs(
          "patientConfirmation.confirmationCancelText",
        )}
        onCancelAction={() => handleConfirmSend(false)}
        onClose={() => setShowConfirmDialog(false)}
      />
    </>
  );
};
